import { useReducer, useCallback } from 'react';
import api from '../services/api';

// Estados iniciais
const initialState = {
  chats: [],
  messages: [],
  currentChat: {},
  messagesPageInfo: { hasMore: false },
  chatsPageInfo: { hasMore: false },
  messagesPage: 1,
  loading: false,
  tab: 0,
  showSearch: false,
  showChatInfo: false,
  highlightedMessage: null,
  typingUsers: new Set(), // ✅ Para compatibilidade com ModernChatMessages
  typingUsersData: new Set(), // ✅ Para ModernChatList (com dados completos)
  chatLastViewed: {},
  // Estados para edição/exclusão
  editingMessage: null,
  deletingMessage: null,
};

// Tipos de ação
export const CHAT_ACTIONS = {
  // Chats
  SET_CHATS: 'SET_CHATS',
  ADD_CHAT: 'ADD_CHAT',
  UPDATE_CHAT: 'UPDATE_CHAT',
  UPDATE_CHAT_AND_REORDER: 'UPDATE_CHAT_AND_REORDER',
  DELETE_CHAT: 'DELETE_CHAT',
  REMOVE_CHAT: 'REMOVE_CHAT',
  SET_CURRENT_CHAT: 'SET_CURRENT_CHAT',
  SELECT_CHAT: 'SELECT_CHAT',
  
  // Mensagens
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  ADD_MESSAGES: 'ADD_MESSAGES',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  DELETE_MESSAGE: 'DELETE_MESSAGE',
  PREPEND_MESSAGES: 'PREPEND_MESSAGES',
  
  // Async operations
  FIND_MESSAGES: 'FIND_MESSAGES',
  
  // Chat metadata
  SET_CHAT_LAST_VIEWED: 'SET_CHAT_LAST_VIEWED',
  
  // UI
  SET_LOADING: 'SET_LOADING',
  SET_TAB: 'SET_TAB',
  TOGGLE_SEARCH: 'TOGGLE_SEARCH',
  TOGGLE_CHAT_INFO: 'TOGGLE_CHAT_INFO',
  SET_HIGHLIGHTED_MESSAGE: 'SET_HIGHLIGHTED_MESSAGE',
  
  // Paginação
  SET_MESSAGES_PAGE: 'SET_MESSAGES_PAGE',
  SET_MESSAGES_PAGE_INFO: 'SET_MESSAGES_PAGE_INFO',
  SET_CHATS_PAGE_INFO: 'SET_CHATS_PAGE_INFO',
    // Digitação
  SET_TYPING_USER: 'SET_TYPING_USER',
  CLEAR_TYPING_USER: 'CLEAR_TYPING_USER',
  
  // Edição/Exclusão
  SET_EDITING_MESSAGE: 'SET_EDITING_MESSAGE',
  SET_DELETING_MESSAGE: 'SET_DELETING_MESSAGE',
  
  // Reset
  RESET_CHAT_STATE: 'RESET_CHAT_STATE',
  RESET_MESSAGES: 'RESET_MESSAGES',
};

// Reducer principal
const chatReducer = (state, action) => {
  switch (action.type) {
    // === CHATS ===
    case CHAT_ACTIONS.SET_CHATS:
      return {
        ...state,
        chats: action.payload,
      };
    
    case CHAT_ACTIONS.ADD_CHAT:
      return {
        ...state,
        chats: [action.payload, ...state.chats],
      };
    
    case CHAT_ACTIONS.UPDATE_CHAT:
      return {
        ...state,
        chats: state.chats.map(chat => 
          chat.id === action.payload.id ? { ...chat, ...action.payload } : chat
        ),
        // Atualizar chat atual se for o mesmo
        currentChat: state.currentChat.id === action.payload.id 
          ? { ...state.currentChat, ...action.payload }
          : state.currentChat,
      };
    
    case CHAT_ACTIONS.UPDATE_CHAT_AND_REORDER:
      // Atualiza o chat e o move para o topo da lista
      const updatedChat = action.payload;
      const filteredChats = state.chats.filter(chat => chat.id !== updatedChat.id);
      return {
        ...state,
        chats: [updatedChat, ...filteredChats],
        currentChat: state.currentChat.id === updatedChat.id 
          ? { ...state.currentChat, ...updatedChat }
          : state.currentChat,
      };
    
    case CHAT_ACTIONS.DELETE_CHAT:
      return {
        ...state,
        chats: state.chats.filter(chat => chat.id !== action.payload),
        // Limpar chat atual se foi deletado
        currentChat: state.currentChat.id === action.payload ? {} : state.currentChat,
        messages: state.currentChat.id === action.payload ? [] : state.messages,
      };
    
    case CHAT_ACTIONS.REMOVE_CHAT:
      // Remove o chat da lista sem afetar o chat atual
      return {
        ...state,
        chats: state.chats.filter(chat => chat.id !== action.payload),
      };
      case CHAT_ACTIONS.SET_CURRENT_CHAT:
      return {
        ...state,
        currentChat: action.payload,
        // Resetar mensagens quando trocar de chat
        messages: [],
        messagesPage: 1,
        messagesPageInfo: { hasMore: false },
        editingMessage: null,
        deletingMessage: null,
        highlightedMessage: null,
      };    case CHAT_ACTIONS.SELECT_CHAT:
      // ✅ Recalcular typingUsers para o novo chat
      const newChatTypingUsers = new Set();
      if (action.payload.id) {
        for (const user of state.typingUsersData) {
          if (user.chatId === action.payload.id) {
            newChatTypingUsers.add(user.userName);
          }
        }
      }
      
      return {
        ...state,
        currentChat: action.payload,
        // Resetar mensagens quando trocar de chat
        messages: [],
        messagesPage: 1,
        messagesPageInfo: { hasMore: false },
        editingMessage: null,
        deletingMessage: null,
        highlightedMessage: null,
        // ✅ Atualizar typingUsers para o novo chat
        typingUsers: newChatTypingUsers,
      };
    
    // === MENSAGENS ===
    case CHAT_ACTIONS.SET_MESSAGES:
      return {
        ...state,
        messages: action.payload,
      };
    
    case CHAT_ACTIONS.ADD_MESSAGE:
      // Evitar duplicatas
      if (state.messages.some(msg => msg.id === action.payload.id)) {
        return state;
      }
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };
    
    case CHAT_ACTIONS.ADD_MESSAGES:
      // Adiciona múltiplas mensagens (para paginação)
      return {
        ...state,
        messages: [...action.payload, ...state.messages],
      };
    
    case CHAT_ACTIONS.UPDATE_MESSAGE:
      return {
        ...state,
        messages: state.messages.map(msg => 
          msg.id === action.payload.id ? { ...msg, ...action.payload } : msg
        ),
        editingMessage: null, // Limpar estado de edição
      };
    
    case CHAT_ACTIONS.DELETE_MESSAGE:
      return {
        ...state,
        messages: state.messages.filter(msg => msg.id !== action.payload),
        deletingMessage: null, // Limpar estado de exclusão
      };
    
    case CHAT_ACTIONS.PREPEND_MESSAGES:
      // Para carregamento de mensagens antigas (load more)
      return {
        ...state,
        messages: [...action.payload, ...state.messages],
      };
    
    // === UI ===
    case CHAT_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    
    case CHAT_ACTIONS.SET_TAB:
      return {
        ...state,
        tab: action.payload,
      };
    
    case CHAT_ACTIONS.TOGGLE_SEARCH:
      return {
        ...state,
        showSearch: !state.showSearch,
        highlightedMessage: state.showSearch ? null : state.highlightedMessage,
      };
    
    case CHAT_ACTIONS.TOGGLE_CHAT_INFO:
      return {
        ...state,
        showChatInfo: !state.showChatInfo,
      };
    
    case CHAT_ACTIONS.SET_HIGHLIGHTED_MESSAGE:
      return {
        ...state,
        highlightedMessage: action.payload,
      };
    
    // === PAGINAÇÃO ===
    case CHAT_ACTIONS.SET_MESSAGES_PAGE:
      return {
        ...state,
        messagesPage: action.payload,
      };
    
    case CHAT_ACTIONS.SET_MESSAGES_PAGE_INFO:
      return {
        ...state,
        messagesPageInfo: action.payload,
      };
    
    case CHAT_ACTIONS.SET_CHATS_PAGE_INFO:
      return {
        ...state,
        chatsPageInfo: action.payload,
      };    // === DIGITAÇÃO ===
    case CHAT_ACTIONS.SET_TYPING_USER:
      const { chatId: setChatId, userId: setUserId, userName: setUserName } = action.payload;
      
      // ✅ Para ModernChatMessages (Set simples de nomes - apenas do chat atual)
      const newTypingUsers = new Set();
      
      // Manter usuários digitando do chat atual e adicionar o novo
      for (const user of state.typingUsersData) {
        if (user.chatId === state.currentChat.id) {
          newTypingUsers.add(user.userName);
        }
      }
      
      // Se o novo usuário é do chat atual, adicionar
      if (setChatId === state.currentChat.id) {
        newTypingUsers.add(setUserName);
      }
      
      // ✅ Para ModernChatList (Set de objetos completos)
      const newTypingUsersData = new Set(state.typingUsersData);
      
      // Remover entradas antigas do mesmo usuário no mesmo chat
      for (const user of newTypingUsersData) {
        if (user.chatId === setChatId && user.userId === setUserId) {
          newTypingUsersData.delete(user);
        }
      }
      
      // Adicionar nova entrada
      newTypingUsersData.add({ chatId: setChatId, userId: setUserId, userName: setUserName });
      
      return {
        ...state,
        typingUsers: newTypingUsers,
        typingUsersData: newTypingUsersData,
      };
    
    case CHAT_ACTIONS.CLEAR_TYPING_USER:
      const { chatId: clearChatId, userId: clearUserId } = action.payload;
      
      // ✅ Para ModernChatMessages - reconstruir Set apenas com usuários do chat atual
      const clearedTypingUsers = new Set();
      
      // ✅ Para ModernChatList - remover do Set de objetos
      const clearedTypingUsersData = new Set();
      
      for (const user of state.typingUsersData) {
        if (!(user.chatId === clearChatId && user.userId === clearUserId)) {
          clearedTypingUsersData.add(user);
          
          // Se for do chat atual, adicionar ao Set simples
          if (user.chatId === state.currentChat.id) {
            clearedTypingUsers.add(user.userName);
          }
        }
      }
      
      return {
        ...state,
        typingUsers: clearedTypingUsers,
        typingUsersData: clearedTypingUsersData,
      };
    
    // === VISUALIZAÇÃO ===
    case CHAT_ACTIONS.SET_CHAT_LAST_VIEWED:
      return {
        ...state,
        chatLastViewed: action.payload,
      };
    
    // === EDIÇÃO/EXCLUSÃO ===
    case CHAT_ACTIONS.SET_EDITING_MESSAGE:
      return {
        ...state,
        editingMessage: action.payload,
        deletingMessage: null, // Não pode editar e deletar ao mesmo tempo
      };
    
    case CHAT_ACTIONS.SET_DELETING_MESSAGE:
      return {
        ...state,
        deletingMessage: action.payload,
        editingMessage: null, // Não pode editar e deletar ao mesmo tempo
      };
    
    // === RESET ===
    case CHAT_ACTIONS.RESET_CHAT_STATE:
      return {
        ...initialState,
        chats: state.chats, // Manter lista de chats
        chatLastViewed: state.chatLastViewed, // Manter histórico de visualização
      };
    
    case CHAT_ACTIONS.RESET_MESSAGES:
      return {
        ...state,
        messages: [],
        messagesPage: 1,
        messagesPageInfo: { hasMore: false },
        editingMessage: null,
        deletingMessage: null,
        highlightedMessage: null,
      };
    
    default:
      return state;
  }
};

// Hook personalizado para usar o reducer
export const useChatReducer = () => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  
  // Actions helpers memoizados
  const actions = {
    // Chats
    setChats: useCallback((chats) => 
      dispatch({ type: CHAT_ACTIONS.SET_CHATS, payload: chats }), []),
    
    addChat: useCallback((chat) => 
      dispatch({ type: CHAT_ACTIONS.ADD_CHAT, payload: chat }), []),
    
    updateChat: useCallback((chat) => 
      dispatch({ type: CHAT_ACTIONS.UPDATE_CHAT, payload: chat }), []),
    
    updateChatAndReorder: useCallback((chat) => 
      dispatch({ type: CHAT_ACTIONS.UPDATE_CHAT_AND_REORDER, payload: chat }), []),
    
    removeChat: useCallback((chatId) => 
      dispatch({ type: CHAT_ACTIONS.REMOVE_CHAT, payload: chatId }), []),
      selectChat: useCallback((chat) => {
      // console.log('🔄 [selectChat] Selecionando chat:', chat.id, chat.title);
      dispatch({ type: CHAT_ACTIONS.SELECT_CHAT, payload: chat });
    }, []),
    
    // Mensagens
    setMessages: useCallback((messages) => 
      dispatch({ type: CHAT_ACTIONS.SET_MESSAGES, payload: messages }), []),
    
    addMessage: useCallback((message) => 
      dispatch({ type: CHAT_ACTIONS.ADD_MESSAGE, payload: message }), []),
    
    addMessages: useCallback((messages) => 
      dispatch({ type: CHAT_ACTIONS.ADD_MESSAGES, payload: messages }), []),
    
    updateMessage: useCallback((message) => 
      dispatch({ type: CHAT_ACTIONS.UPDATE_MESSAGE, payload: message }), []),
    
    deleteMessage: useCallback((messageId) => 
      dispatch({ type: CHAT_ACTIONS.DELETE_MESSAGE, payload: messageId }), []),
    
    // Chat metadata
    setChatLastViewed: useCallback((chatLastViewed) => 
      dispatch({ type: CHAT_ACTIONS.SET_CHAT_LAST_VIEWED, payload: chatLastViewed }), []),
    
    // Typing
    setTypingUser: useCallback((chatId, userId, userName, isTyping) => {
      if (isTyping) {
        dispatch({ type: CHAT_ACTIONS.SET_TYPING_USER, payload: { chatId, userId, userName } });
      } else {
        dispatch({ type: CHAT_ACTIONS.CLEAR_TYPING_USER, payload: { chatId, userId } });
      }
    }, []),
    
    // UI
    setLoading: useCallback((loading) => 
      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: loading }), []),
    
    setTab: useCallback((tab) => 
      dispatch({ type: CHAT_ACTIONS.SET_TAB, payload: tab }), []),
    
    toggleSearch: useCallback(() => 
      dispatch({ type: CHAT_ACTIONS.TOGGLE_SEARCH }), []),
    
    toggleChatInfo: useCallback(() => 
      dispatch({ type: CHAT_ACTIONS.TOGGLE_CHAT_INFO }), []),
    
    setHighlightedMessage: useCallback((messageId) => 
      dispatch({ type: CHAT_ACTIONS.SET_HIGHLIGHTED_MESSAGE, payload: messageId }), []),
    
    // Paginação
    setMessagesPage: useCallback((page) => 
      dispatch({ type: CHAT_ACTIONS.SET_MESSAGES_PAGE, payload: page }), []),
    
    setMessagesPageInfo: useCallback((pageInfo) => 
      dispatch({ type: CHAT_ACTIONS.SET_MESSAGES_PAGE_INFO, payload: pageInfo }), []),
    
    setChatsPageInfo: useCallback((pageInfo) => 
      dispatch({ type: CHAT_ACTIONS.SET_CHATS_PAGE_INFO, payload: pageInfo }), []),
    
    // Digitação
    addTypingUser: useCallback((userId) => 
      dispatch({ type: CHAT_ACTIONS.SET_TYPING_USER, payload: userId }), []),
    
    removeTypingUser: useCallback((userId) => 
      dispatch({ type: CHAT_ACTIONS.CLEAR_TYPING_USER, payload: userId }), []),
    
    // Visualização
    setChatLastViewed: useCallback((chatId, timestamp) => 
      dispatch({ type: CHAT_ACTIONS.SET_CHAT_LAST_VIEWED, payload: { chatId, timestamp } }), []),
    
    // Edição/Exclusão
    setEditingMessage: useCallback((message) => 
      dispatch({ type: CHAT_ACTIONS.SET_EDITING_MESSAGE, payload: message }), []),
    
    setDeletingMessage: useCallback((message) => 
      dispatch({ type: CHAT_ACTIONS.SET_DELETING_MESSAGE, payload: message }), []),
    
    // Reset
    resetChatState: useCallback(() => 
      dispatch({ type: CHAT_ACTIONS.RESET_CHAT_STATE }), []),
    
    resetMessages: useCallback(() => 
      dispatch({ type: CHAT_ACTIONS.RESET_MESSAGES }), []),
    
    // Async operations
    findMessages: useCallback(async (chatId) => {
      dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: true });
      try {
        const api = (await import('../services/api')).default;
        const { data } = await api.get(`/chats/${chatId}/messages?pageNumber=1`);
        dispatch({ type: CHAT_ACTIONS.SET_MESSAGES_PAGE_INFO, payload: data });
        dispatch({ type: CHAT_ACTIONS.SET_MESSAGES, payload: data.records });
        dispatch({ type: CHAT_ACTIONS.SET_MESSAGES_PAGE, payload: 2 });
        return data;
      } catch (err) {
        console.error("Erro ao carregar mensagens:", err);
        throw err;
      } finally {
        dispatch({ type: CHAT_ACTIONS.SET_LOADING, payload: false });
      }
    }, []),
  };
  
  return [state, actions];
};

// Seletores para performance
export const chatSelectors = {
  // Chats organizados
  getOrganizedChats: (state, currentUserId) => {
    const pinnedChats = [];
    const normalChats = [];
    const archivedChats = [];
    
    state.chats.forEach(chat => {
      const chatUser = chat.users?.find(u => u.userId === currentUserId);
      
      if (chatUser?.isArchived) {
        archivedChats.push(chat);
      } else if (chatUser?.isPinned) {
        pinnedChats.push(chat);
      } else {
        normalChats.push(chat);
      }
    });
    
    return { pinnedChats, normalChats, archivedChats };
  },
  
  // Mensagens agrupadas por data
  getGroupedMessages: (state) => {
    const groups = [];
    let currentGroup = null;
    
    state.messages.forEach(message => {
      const messageDate = new Date(message.createdAt).toDateString();
      
      if (!currentGroup || currentGroup.date !== messageDate) {
        currentGroup = {
          date: messageDate,
          messages: []
        };
        groups.push(currentGroup);
      }
      
      currentGroup.messages.push(message);
    });
    
    return groups;
  },
  
  // Verifica se mensagem pode ser editada/deletada (5 minutos)
  canEditOrDeleteMessage: (message, currentUserId) => {
    if (message.senderId !== currentUserId) return false;
    
    const messageTime = new Date(message.createdAt);
    const now = new Date();
    const diffMinutes = (now - messageTime) / (1000 * 60);
    
    return diffMinutes <= 5;
  },
  
  // Contador de mensagens não lidas
  getUnreadCount: (chat, state, currentUserId) => {
    if (!chat || state.currentChat.id === chat.id) return 0;
    
    const chatUser = chat.users?.find(u => u.userId === currentUserId);
    if (chatUser?.isMuted) return 0;
    
    return chatUser?.unreads || 0;
  }
};

export default useChatReducer;
